/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import { ChevronRight, Component } from 'lucide-react';
import { setActiveModal } from 'modules/template-editor/store/editor/editor';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import UDTippy from 'ud-ui/components/tippy';
import history from 'ud-ui/components/fabric-canvas/addons/history';
import { Placeholders } from 'ud-ui/components/fabric-canvas/addons/placeholders';
import SnapToGrid from 'ud-ui/components/fabric-canvas/addons/snap-to-grid';
import { commonPlaceholder } from 'ud-ui/components/fabric-canvas/types/placeholders/create-argument';
import { ModalState } from '../../../../../domain/interface/modal-state';
import { useGetBoardType } from '../../../../../domain/utils/use-get-board-type';
import { selectActiveModal, selectFabricManager } from '../../../../../store/editor/selectors';

import BaseModal from '../../../elements/modals/base-modal';
import { IconButton, ModalButton, OpenerIconWrapper } from '../styles';
import { carouselPlaceholderList } from './carousel-placeholder-list';
import { categoryList } from './category-list';
import { commonPlaceholderList } from './common-placeholder-list';
import { complexPlaceholdersList } from './complex-placeholders-list';
import { eventPlaceholderList } from './event-placeholder-list';
import { useCloseOnOutsideOrEsc } from 'shared/utils';

export function AddPlaceholder() {
  const dispatch = useDispatch();
  const activeModal = useSelector(selectActiveModal);
  const fabricManager = useSelector(selectFabricManager);
  const [isSelected, setSelected] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const [categoryModalState, setCategoryModalState] = useState<ModalState>({ isModalOpen: false, modalStyle: {} });
  const [modalPlaceholderState, setModalPlaceholderState] = useState<ModalState>({ isModalOpen: false, modalStyle: {} });
  const boardType = useGetBoardType();

  const placeholders = useMemo(() => (fabricManager ? new Placeholders(fabricManager) : undefined), [fabricManager]);

  const closeModals = useCallback(() => {
    console.trace('closeModals');
    setSelected(false);
    setCategoryModalState((prev) => ({ ...prev, isModalOpen: false }));
    setModalPlaceholderState((prev) => ({ ...prev, isModalOpen: false }));
    dispatch(setActiveModal(undefined));
  }, [dispatch]);

  const openCategoryModal = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const target = e.currentTarget;
      if (!target) return;

      const bounding = target.getBoundingClientRect();
      setCategoryModalState({
        isModalOpen: true,
        modalStyle: {
          left: `${bounding.left + bounding.width + 15}px`,
          width: 150,
        },
      });
      setSelected(true);
      dispatch(setActiveModal('addPlaceholder'));
    },
    [dispatch],
  );

  useCloseOnOutsideOrEsc({
    isActive: isSelected,
    isInsideSelectors: ['.modal-button', '.base-modal', '.icon-button-placeholder', '.category-button'],
    onClose: closeModals,
  });

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isSelected) {
        closeModals();
      } else {
        openCategoryModal(e);
      }
    },
    [closeModals, isSelected, openCategoryModal],
  );

  const addPlaceholder = (props: commonPlaceholder) => {
    if (!fabricManager || !placeholders) return;

    history.saveState();
    const placeholder = placeholders.common.create(props);
    SnapToGrid.snapToGrid([placeholder]);
    closeModals();
  };

  const handleComplexPlaceholder = (placeholder: any) => {
    if (!fabricManager || !placeholders) return;

    history.saveState();
    placeholders.complex.create(placeholder);
    closeModals();
  };

  const handleEventPlaceholder = (placeholder: any) => {
    if (!fabricManager || !placeholders) return;

    history.saveState();
    const object = placeholders.event.create(placeholder);
    SnapToGrid.snapToGrid([object]);
    closeModals();
  };

  const handleCarouselPlaceholder = (placeholder: any) => {
    if (!fabricManager || !placeholders) return;

    history.saveState();
    const object = placeholders.carousel.create(placeholder);
    SnapToGrid.snapToGrid([object]);
    closeModals();
  };

  const openPlaceholderModal = (e: React.MouseEvent<HTMLButtonElement>, category: string) => {
    e.stopPropagation();
    const target = e.currentTarget;
    if (!target) return;

    const bounding = target.getBoundingClientRect();
    setModalPlaceholderState({
      isModalOpen: true,
      modalStyle: {
        top: `${bounding.top - 8}px`,
        left: `${bounding.left + bounding.width + 2}px`,
        width: 300,
      },
    });
    setSelectedCategory(category);
  };

  const renderPlaceholderButtons = () => {
    switch (selectedCategory) {
      case 'common':
        return commonPlaceholderList.map(({ listName, indicator, blockText, type, teamIndex }) => (
          <ModalButton
            className="modal-button"
            key={indicator}
            onClick={() => addPlaceholder({ indicator, blockText, type, teamIndex })}
          >
            {listName}
          </ModalButton>
        ));
      case 'complex':
        return complexPlaceholdersList.map((placeholder) => (
          <ModalButton
            className="modal-button"
            key={placeholder.indicator}
            onClick={(e) => {
              e.stopPropagation();
              handleComplexPlaceholder(placeholder);
            }}
          >
            {placeholder.listName}
          </ModalButton>
        ));
      case 'events':
        return eventPlaceholderList.map((placeholder) => (
          <ModalButton
            className="modal-button"
            key={placeholder.indicator}
            onClick={(e) => {
              e.stopPropagation();
              handleEventPlaceholder(placeholder);
            }}
          >
            {placeholder.listName}
          </ModalButton>
        ));
      case 'carousel':
        return carouselPlaceholderList.map((placeholder) => (
          <ModalButton
            className="modal-button"
            key={placeholder.indicator}
            onClick={(e) => {
              e.stopPropagation();
              handleCarouselPlaceholder(placeholder);
            }}
          >
            {placeholder.listName}
          </ModalButton>
        ));
      default:
        return null;
    }
  };

  const filteredCategories = useMemo(
    () =>
      categoryList.filter((category) => {
        if (boardType === 'events') return category.value !== 'carousel';
        if (boardType === 'carousel') return category.value !== 'events';
        return category.value !== 'events' && category.value !== 'carousel';
      }),
    [boardType],
  );

  return (
    <>
      <UDTippy disabled={Boolean(activeModal)} content={'Блоки'} size="xs">
        <span style={{ display: 'inline-block' }}>
          <IconButton
            className="icon-button-placeholder"
            style={{ color: activeModal === 'addPlaceholder' ? '#D9B244' : undefined }}
            onClick={handleClick}
            tabIndex={0}
          >
            <OpenerIconWrapper icon={<Component size={20} strokeWidth={1.5} />} />
          </IconButton>
        </span>
      </UDTippy>

      <BaseModal className="base-modal" isOpen={categoryModalState.isModalOpen && isSelected} style={categoryModalState.modalStyle}>
        {filteredCategories.map((category) => (
          <ModalButton
            className="category-button modal-button"
            key={category.value}
            onClick={(event) => openPlaceholderModal(event, category.value)}
            style={{ justifyContent: 'space-between' }}
          >
            {category.label}
            <div
              css={css`
                transition: color 0.25s ease;
                color: ${category.value === selectedCategory ? '#D9B244' : '#8F8F8F'};
              `}
            >
              <ChevronRight size={13} />
            </div>
          </ModalButton>
        ))}
      </BaseModal>

      <BaseModal
        className="base-modal"
        isOpen={modalPlaceholderState.isModalOpen && isSelected}
        style={modalPlaceholderState.modalStyle}
      >
        {renderPlaceholderButtons()}
      </BaseModal>
    </>
  );
}
